import { useState } from "react";

import { useCurrentWorkspace, useFeatureFlag } from "@accurx/auth";
import * as UI from "@accurx/design";
import { Ds } from "@accurx/design";
import { PatientExternalId, useMedicalRecordConnection } from "@accurx/native";
import { containsAbuseWord } from "@accurx/shared/dist/AbuseWordsHelper";
import { useComposeMessages } from "domains/message-component/hooks/useComposeMessages";
import {
    ComposeMessageEditorErrorType,
    ComposeWarning,
    MessageEditorId,
} from "domains/message-component/reducer.types";
import {
    validateCompose,
    validateMessage,
} from "domains/message-component/utils/validation/validateMessage";

import { ComposeProps } from "../../types";
import { OnMessageSendFn } from "../../types/Compose.types";
import { useCommunicationConsent } from "../RecipientSelector/CommunicationConsent/ConsentAggregate/useCommunicationConsent";
import { SendSplitButton } from "../SendSplitButton/SendSplitButton";
import { AbuseWordCheckModal } from "./AbuseWordCheckModal";
import { ConsentStillLoadingModal } from "./ConsentStillLoadingModal";
import { DeclinedConsentModal } from "./DeclinedConsentModal";
import { FailedAttachmentWarningModal } from "./FailedAttachmentWarningModal";
import {
    ForgotAllowReplyModal,
    listOfForgotReplyPhrases,
} from "./ForgotAllowReplyModal";
import { MixedConsentModal } from "./MixedConsentModal";
import { SaveToRecordWithDifferentEmrPatientModal } from "./SaveToRecordWithDifferentEmrPatient";
import { prepareSendMessage } from "./prepareSendMessage";

type SendMessageProps = Pick<
    ComposeProps,
    "patientMatchesCurrentMedicalRecordPatient"
> & {
    patientExternalIds: PatientExternalId[];
    onMessageSend: OnMessageSendFn;
    isLoading: boolean;
};

export const SendMessage = ({
    patientMatchesCurrentMedicalRecordPatient,
    patientExternalIds,
    onMessageSend,
    isLoading,
}: SendMessageProps) => {
    const workspace = useCurrentWorkspace();
    const { state, dispatch, isMultipleMessages } = useComposeMessages();
    const [warnings, setWarnings] = useState<ComposeWarning[]>([]);
    const [allowReplyWarningTriggers, setAllowReplyWarningTrigger] = useState<
        string[]
    >([]);

    function clearWarnings() {
        setAllowReplyWarningTrigger([]);
        setWarnings([]);
    }
    // Medical record
    const connection = useMedicalRecordConnection();

    const isAllowReplyReminderEnabled = useFeatureFlag(
        "ComposeAllowReplyReminder",
    );

    const communicationConsent = useCommunicationConsent({
        patientExternalIds,
    });

    const shouldSaveToRecord =
        connection.status === "Connected" &&
        connection.capabilities.saveToRecord &&
        state.isSaveToRecordEnabled;

    const handleValidatedMessageSendClick = (args: {
        /** Whether the button to send has been clicked from a warning modal */
        isModal: boolean;
        warnings: string[];
        errors: string[];
    }) => {
        onMessageSend({
            errors: args.errors,
            warnings: args.warnings,
            isModal: args.isModal,
            data: prepareSendMessage({
                state,
                workspaceName: workspace.organisationName,
            }),
        });
    };

    const getCurrentWarnings = (): ComposeWarning[] => {
        const warnings: ComposeWarning[] = [];

        if (
            state.messages.find((message) =>
                containsAbuseWord(
                    `${message.messageBody} ${message.messageSignature}`,
                ),
            )
        ) {
            warnings.push("ABUSE_WORD");
        }

        if (
            state.messages.find((message) =>
                message.attachments.some(({ status }) => status === "error"),
            )
        ) {
            warnings.push("FAILED_ATTACHMENT");
        }

        if (
            shouldSaveToRecord &&
            // Important to check for false value as null means undetermined
            patientMatchesCurrentMedicalRecordPatient === false
        ) {
            warnings.push("SAVE_TO_RECORD_WITH_DIFFERENT_EMR_PATIENT");
        }

        /* Check isFetching as we cannot reply on status === "loading", as
        /* as the query will be in this state when disabled */
        if (communicationConsent?.isFetching) {
            warnings.push("CONSENT_STILL_LOADING");
        }

        if (communicationConsent?.status === "success") {
            const consentAggregate =
                state.contactDetails.method === "Email"
                    ? communicationConsent.data.emailConsent.consentAggregate
                    : communicationConsent.data.smsConsent.consentAggregate;
            if (consentAggregate === "Mixed") {
                warnings.push("MIXED_CONSENT");
            }
            if (consentAggregate === "AllDeclined") {
                warnings.push("DECLINED_CONSENT");
            }
        }

        const messageForReplyReminder = state.messages[0];
        if (
            isAllowReplyReminderEnabled &&
            state.template.type !== "QuestionnaireTemplate" &&
            state.template.type !== "PendingQuestionnaireTemplate" &&
            state.template.type !== "PathwayTemplate" &&
            !messageForReplyReminder.isPatientResponseEnabled &&
            !messageForReplyReminder.selfBookLink
        ) {
            const body =
                state.template.type === "MessageTemplate"
                    ? state.template.value.body
                    : "";

            const hasTriggerInTemplate =
                !!body &&
                listOfForgotReplyPhrases.some(({ regex }) => body.match(regex));

            // Don't warn based on the content of a template
            if (!hasTriggerInTemplate) {
                const matchingTriggers = listOfForgotReplyPhrases.flatMap(
                    ({ label, regex }) => {
                        if (messageForReplyReminder.messageBody.match(regex)) {
                            return [label];
                        }
                        return [];
                    },
                );
                setAllowReplyWarningTrigger(matchingTriggers);

                if (matchingTriggers.length > 0) {
                    warnings.push("FORGOT_ALLOW_REPLY");
                }
            }
        }

        return warnings;
    };

    const handleMessageValidation = () => {
        let hasErrors = false;
        const messageEditorErrors: Record<
            MessageEditorId,
            ComposeMessageEditorErrorType
        > = {};
        const analyticsErrors: string[] = [];

        // Check for top level errors
        const topLevelErrors = validateCompose({
            contactMethod: state.contactDetails.method,
        });

        if (Object.keys(topLevelErrors).length > 0) {
            hasErrors = true;
            analyticsErrors.push(...Object.values(topLevelErrors));
        }

        // Validate each message editor
        state.messages.forEach((message, i) => {
            const errors = validateMessage({
                workspaceName: workspace.organisationName,
                originalGreeting: state.originalGreeting,
                originalSignature: state.originalSignature,
                messageBody: message.messageBody,
                messageSignature: message.messageSignature,
                nhsAdviceLink: message.nhsAdviceLink,
                withSelfBookLink: !!message.selfBookLink,
                contactMethod: state.contactDetails.method,
                attachments: message.attachments,
                withPatientResponseEnabled: message.isPatientResponseEnabled,
                template: state.template,
                sendAtDateTime: message.sendAt?.sendAtDateTime ?? null,
            });

            if (Object.keys(errors).length > 0) {
                hasErrors = true;
                analyticsErrors.push(...Object.values(errors));
                messageEditorErrors[i] = errors;
            }
        });

        dispatch({
            type: "SET_ERRORS",
            payload: {
                topLevelErrors,
                messageEditorErrors,
            },
        });

        if (hasErrors) {
            handleValidatedMessageSendClick({
                errors: analyticsErrors,
                warnings: [],
                isModal: false,
            });
            return;
        }

        const warnings = getCurrentWarnings();
        if (warnings.length > 0) {
            setWarnings(warnings);
            handleValidatedMessageSendClick({
                errors: analyticsErrors,
                warnings,
                isModal: false,
            });
            return;
        }

        handleValidatedMessageSendClick({
            isModal: false,
            errors: [],
            warnings: [],
        });
    };

    const trySendMessageOrNextWarning = () => {
        if (warnings.length > 1) {
            setWarnings((prev) => prev.slice(1));
        } else {
            clearWarnings();
            handleValidatedMessageSendClick({
                isModal: true,
                errors: [],
                warnings: [],
            });
        }
    };

    return (
        <>
            {isMultipleMessages ? (
                <Ds.Button
                    size="small"
                    disabled={
                        isLoading || state.messages.some((m) => m.isScheduling)
                    }
                    onClick={handleMessageValidation}
                >
                    <Ds.Icon
                        name="Clock"
                        color="white"
                        size="small"
                        appearance="outline"
                        aria-hidden={true}
                    />
                    Send
                </Ds.Button>
            ) : (
                <SendSplitButton
                    isSending={isLoading}
                    sendMessageHandler={handleMessageValidation}
                />
            )}
            <AbuseWordCheckModal
                isOpen={warnings[0] === "ABUSE_WORD"}
                onClose={clearWarnings}
                onSendMessage={() => {
                    trySendMessageOrNextWarning();
                }}
            />
            <ForgotAllowReplyModal
                isOpen={warnings[0] === "FORGOT_ALLOW_REPLY"}
                onClose={clearWarnings}
                triggers={allowReplyWarningTriggers}
                onSendMessage={() => {
                    trySendMessageOrNextWarning();
                }}
            />
            <FailedAttachmentWarningModal
                isOpen={warnings[0] === "FAILED_ATTACHMENT"}
                onClose={clearWarnings}
                onClickSendMessageWithoutAttachment={() => {
                    trySendMessageOrNextWarning();
                }}
            />
            {connection.status === "Connected" && (
                <SaveToRecordWithDifferentEmrPatientModal
                    isOpen={
                        warnings[0] ===
                        "SAVE_TO_RECORD_WITH_DIFFERENT_EMR_PATIENT"
                    }
                    onCancel={clearWarnings}
                    onSendMessage={() => {
                        trySendMessageOrNextWarning();
                    }}
                    medicalRecordSystem={connection.system}
                />
            )}

            {communicationConsent !== null && (
                <UI.VisuallyHidden>
                    <span
                        data-testid={`consent-status-${communicationConsent.status}`}
                    />
                </UI.VisuallyHidden>
            )}

            <MixedConsentModal
                isOpen={warnings[0] === "MIXED_CONSENT"}
                onClose={clearWarnings}
                onSendMessage={() => {
                    trySendMessageOrNextWarning();
                }}
                method={
                    state.contactDetails.method === "None"
                        ? "Mobile"
                        : state.contactDetails.method
                }
            />

            <DeclinedConsentModal
                isOpen={warnings[0] === "DECLINED_CONSENT"}
                onClose={clearWarnings}
                onSendMessage={() => {
                    trySendMessageOrNextWarning();
                }}
                method={
                    state.contactDetails.method === "None"
                        ? "Mobile"
                        : state.contactDetails.method
                }
            />

            <ConsentStillLoadingModal
                isOpen={warnings[0] === "CONSENT_STILL_LOADING"}
                onClose={clearWarnings}
                onSendMessage={() => {
                    trySendMessageOrNextWarning();
                }}
            />
        </>
    );
};
